import { useState } from "react";
import { Link, useLocation } from "wouter";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { Loader2 } from "lucide-react";
import bgDecorative from "@/assets/onboarding/bg-decorative.svg";
import logo from "@/assets/Logo.png";
import LanguageSwitcher from "@/components/layout/LanguageSwitcher";
export default function Login() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Login failed");
      }

      const data = await response.json();
      const user = data.user;

      toast({
        title: "Welcome back!",
        description: "Successfully logged in.",
      });

      // Redirect based on user role and workspace role
      let redirectUrl = "/assessments";
      if (user.role === "admin") {
        redirectUrl = "/admin/dashboard";
      } else if (user.role === "platform_assistant") {
        redirectUrl = "/assistant/dashboard";
      } else if (user.workspaceRole === "workspace_admin") {
        redirectUrl = "/dashboard";
      } else if (user.workspaceRole === "member") {
        redirectUrl = "/assessments";
      }

      window.location.href = redirectUrl;
    } catch (error) {
      toast({
        title: "Login failed",
        description: error instanceof Error ? error.message : "Invalid credentials",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col relative"
      style={{
      backgroundImage: `url(${bgDecorative})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Language switcher — top-right */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSwitcher />
      </div>

    <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-[560px] bg-white rounded-3xl shadow-[0px_8px_10px_-6px_rgba(0,0,0,0.10)] shadow-xl outline outline-1 outline-offset-[-1px] outline-slate-200 overflow-hidden">
          <form onSubmit={handleLogin} className="p-16 flex flex-col items-center gap-8">
          {/* Logo */}
          <img className="w-24 h-9 object-contain" src={logo} alt="180Pi" />

          {/* Header */}
          <div className="w-full flex flex-col items-center gap-2">
            <h1 className="text-center text-3xl font-extrabold leading-9" style={{ color: "#00444C" }}>
              {"Welcome Back"}
            </h1>
            <p className="text-center text-slate-600 text-base font-normal leading-6">
              {"Enter your credentials to access your account"}
            </p>
          </div>

          {/* Form fields */}
          <div className="w-full flex flex-col gap-5">
            {/* Email */}
            <div className="flex flex-col gap-2">
              <Label className="text-slate-700 text-sm font-semibold leading-5">
                {"Email"}
              </Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={"name@company.com"}
                required
                className="px-4 py-3 bg-white rounded-2xl outline outline-1 outline-offset-[-1px] outline-slate-200 text-sm text-slate-700 placeholder:text-gray-500"
              />
            </div>

            {/* Password */}
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <Label className="text-slate-700 text-sm font-semibold leading-5">
                  {"Password"}
                </Label>
                <Link
                  href="/forgot-password"
                  className="text-sm font-medium leading-5"
                  style={{ color: "#00444C" }}
                >
                  {"Forgot password?"}
                </Link>
              </div>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="px-4 py-3 bg-white rounded-2xl outline outline-1 outline-offset-[-1px] outline-slate-200 text-sm text-slate-700 placeholder:text-gray-500"
              />
            </div>

            {/* Sign In button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full px-4 py-3 rounded-2xl shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] flex justify-center items-center text-white text-sm font-bold leading-5 disabled:opacity-50 cursor-pointer transition-opacity hover:opacity-90"
              style={{ backgroundColor: "#00444C" }}
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                "Login"
              )}
            </button>
          </div>
        </form>
      </div>
      </main>
    </div>
  );
}
